import React, { useCallback, useState } from "react"
import styled, { css } from "styled-components"
import { StyledLink } from "../../styles/base-styles"
import { WpPost } from "../../types/wordpress-types"
import { CategoryThumbnail } from "../common/category-image"
import { Icons } from "../icons/icons"
import useSiteMetadata from "../../hooks/use-site-metadata"

export const PostNavigation: React.FC<{
  next?: WpPost
  prev?: WpPost
  path: string
}> = ({ prev, next, path }) => {
  const { siteUrl } = useSiteMetadata()

  const nextCategory = next ? next.categories.nodes[0]?.databaseId : undefined
  const nextPath = next ? `/${next.databaseId}` : undefined

  const prevCategory = prev ? prev.categories.nodes[0]?.databaseId : undefined
  const prevPath = prev ? `/${prev.databaseId}` : undefined

  const [isMouseEnteredInNextPostDiv, setIsMouseEnteredInNextPostDiv] =
    useState(false)
  const [isMouseEnteredInPrevPostDiv, setIsMouseEnteredInPrevPostDiv] =
    useState(false)

  const handleMouseEnterInNextPostDiv = useCallback(() => {
    setIsMouseEnteredInNextPostDiv(true)
  }, [])

  const handleMouseLeaveFromNextPostDiv = useCallback(() => {
    setIsMouseEnteredInNextPostDiv(false)
  }, [])

  const handleMouseEnterInPrevPostDiv = useCallback(() => {
    setIsMouseEnteredInPrevPostDiv(true)
  }, [])

  const handleMouseLeaveFromPrevPostDiv = useCallback(() => {
    setIsMouseEnteredInPrevPostDiv(false)
  }, [])

  return next || prev ? (
    <PostNavigationDiv>
      {next ? (
        <NextPost
          to={nextPath!}
          onMouseEnter={handleMouseEnterInNextPostDiv}
          onMouseLeave={handleMouseLeaveFromNextPostDiv}
        >
          <PostInfoBox next>
            <ThumbnailDiv>
              <CategoryThumbnail
                width={103}
                height={54}
                mobileWidth={80}
                mobileHeight={42}
                featureImageUrl={next.featuredImage?.node.sourceUrl}
                categoryDatabaseId={nextCategory}
                className="post_thumbnail_image"
                gatsbyImage={next.featuredImage?.node.gatsbyImage}
              />
            </ThumbnailDiv>
            <div style={{ flex: "1 1 auto" }}>
              <PostTitle>{next.title}</PostTitle>
            </div>
          </PostInfoBox>
          <DirectionBox next>
            <Icons.CategoryChevron
              width={12}
              direction={"left"}
              changeColorOnActive
              isActive={isMouseEnteredInNextPostDiv}
              activeColor="#ef7a00"
            />
            <p>前の記事へ</p>
          </DirectionBox>
        </NextPost>
      ) : (
        <div />
      )}
      <HorizontalSeparator />
      {prev ? (
        <PrevPost
          to={prevPath!}
          onMouseEnter={handleMouseEnterInPrevPostDiv}
          onMouseLeave={handleMouseLeaveFromPrevPostDiv}
        >
          <PostInfoBox prev>
            <div style={{ flex: "1 1 auto" }}>
              <PostTitle>{prev.title}</PostTitle>
            </div>
            <ThumbnailDiv>
              <CategoryThumbnail
                width={103}
                height={54}
                mobileWidth={80}
                mobileHeight={42}
                featureImageUrl={prev.featuredImage?.node.sourceUrl}
                categoryDatabaseId={prevCategory}
                className="post_thumbnail_image"
                gatsbyImage={prev.featuredImage?.node.gatsbyImage}
              />
            </ThumbnailDiv>
          </PostInfoBox>
          <DirectionBox prev>
            <p>次の記事へ</p>
            <Icons.CategoryChevron
              width={12}
              changeColorOnActive
              isActive={isMouseEnteredInPrevPostDiv}
              activeColor="#ef7a00"
            />
          </DirectionBox>
        </PrevPost>
      ) : (
        <div />
      )}
    </PostNavigationDiv>
  ) : null
}

const PostNavigationDiv = styled.div`
  background: white;
  display: grid;
  grid-template-columns: 1fr 1fr;
  position: relative;
  border-top: 1px solid #d8d8d8;
  border-bottom: 1px solid #d8d8d8;
  margin-top: 32px;
`
const PostNavigationElementDiv = styled(StyledLink)`
  display: flex;
  flex-flow: column;
  justify-content: center;
  gap: 12px;
  align-items: center;
  padding: 16px;
  color: var(--Dark_Gray, #242424);
  transition: transform 0.5s;

  .post_thumbnail_image {
    transition: transform 0.5s;
  }

  &:hover {
    color: #ef7a00 !important;
    .post_thumbnail_image {
      transform: scale(1.2);
      transition: transform 0.5s;
    }
  }
  /* SP style */
  @media screen and (max-width: 767px) {
    gap: 2px;
  }
`

const PrevPost = styled(PostNavigationElementDiv)`
  grid-template-columns: 1fr 100px 31px;
  padding-left: 20px;
`

const NextPost = styled(PostNavigationElementDiv)`
  padding-right: 20px;
`

const PostInfoBox = styled.div<{ next?: boolean; prev?: boolean }>`
  display: flex;
  justify-content: flex-start;
  width: 100%;
  gap: 12px;
  height: 75px;
  ${({ prev }) =>
    prev &&
    css`
      justify-content: flex-end;
    `}
  /* SP style */
  @media screen and (max-width: 767px) {
    height: 180px;
    flex-flow: column;
    gap: 0;
    ${({ prev }) =>
      prev &&
      css`
        align-items: flex-end;
        flex-flow: column-reverse;
      `}
  }
`
const ThumbnailDiv = styled.div`
  flex: 0 0 80px;
  /* SP style */
  @media screen and (max-width: 767px) {
    flex: 0 0 54px;
  }
`
const PostTitle = styled.p`
  font-feature-settings: "clig" off, "liga" off;
  font-family: san-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: bold;
  line-height: 25.2px;
  letter-spacing: 1.28px;
  max-width: 100%;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  word-wrap: break-word;
  word-break: break-all;
  /* SP style */
  @media screen and (max-width: 767px) {
    -webkit-line-clamp: 5;
  }
`

export const DirectionBox = styled.div<{ next?: boolean; prev?: boolean }>`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  ${({ prev }) =>
    prev &&
    css`
      justify-content: flex-end;
    `}

  p {
    font-family: san-serif;
    font-size: 11px;
    font-style: normal;
    font-weight: 700;
    line-height: 16px; /* 145.455% */
    text-transform: uppercase;
  }
`

const HorizontalSeparator = styled.div`
  position: absolute;
  background-color: #d8d8d8;
  height: 45px;
  width: 1px;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`
